import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Mail, IdCard, CheckCircle, Clock, XCircle, Upload, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const ClientVerificationPage = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Email code flow
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [code, setCode] = useState('');
  const [confirming, setConfirming] = useState(false);

  // ID upload
  const [idType, setIdType] = useState('dui');
  const [uploading, setUploading] = useState(false);
  const idInputRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/verification/status`);
      setStatus(res.data);
    } catch {
      toast.error(t('verify.load_error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (user?.role !== 'client') {
      navigate('/dashboard');
      return;
    }
    fetchStatus();
  }, [user, navigate, fetchStatus]);

  const sendCode = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/verification/email/send`);
      if (res.data.already_verified) {
        await fetchStatus();
        await refreshUser();
        return;
      }
      setCodeSent(true);
      if (res.data.dev_code) {
        toast.info(`Dev code: ${res.data.dev_code}`, { duration: 20000 });
      } else {
        toast.success(t('verify.code_sent'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('verify.code_send_failed'));
    } finally {
      setSending(false);
    }
  };

  const confirmCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/verification/email/confirm`, { code: code.trim() });
      toast.success(t('verify.email_verified'));
      setCode('');
      setCodeSent(false);
      await fetchStatus();
      await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('verify.code_invalid'));
    } finally {
      setConfirming(false);
    }
  };

  const handleIdFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', idType);
      await axios.post(`${API}/clients/identity/upload`, fd);
      toast.success(t('verify.id_uploaded'));
      await fetchStatus();
      await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('verify.upload_failed'));
    } finally {
      setUploading(false);
      if (idInputRef.current) idInputRef.current.value = '';
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  }

  const emailVerified = !!status?.email_verified;
  const idStatus = status?.client_verification_status || 'not_submitted';
  const hasId = !!status?.has_id_document;
  const fullyReady = emailVerified && hasId && idStatus !== 'rejected';

  const IdStatusBadge = () => {
    if (idStatus === 'approved') {
      return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="w-3.5 h-3.5 mr-1 inline" />{t('verify.id_approved')}</Badge>;
    }
    if (idStatus === 'pending') {
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200"><Clock className="w-3.5 h-3.5 mr-1 inline" />{t('verify.id_pending')}</Badge>;
    }
    if (idStatus === 'rejected') {
      return <Badge className="bg-red-100 text-red-800 border-red-200"><XCircle className="w-3.5 h-3.5 mr-1 inline" />{t('verify.id_rejected')}</Badge>;
    }
    return <Badge variant="outline">{t('verify.id_not_submitted')}</Badge>;
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit' }}>{t('verify.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('verify.subtitle')}</p>
      </div>

      {fullyReady && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-green-800 text-sm">
          <ShieldCheck className="w-5 h-5 flex-shrink-0" />
          <span>{t('verify.all_set')}</span>
        </div>
      )}

      {/* ── Email verification ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4" />{t('verify.email_title')}
            {emailVerified && <CheckCircle className="w-4 h-4 text-green-500" />}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{user?.email}</p>
          {emailVerified ? (
            <Badge className="bg-green-100 text-green-800 border-green-200">
              <CheckCircle className="w-3.5 h-3.5 mr-1 inline" />{t('verify.email_verified_badge')}
            </Badge>
          ) : !codeSent ? (
            <Button onClick={sendCode} disabled={sending} size="sm">
              {sending ? t('verify.sending') : t('verify.send_code')}
            </Button>
          ) : (
            <form onSubmit={confirmCode} className="space-y-3">
              <div>
                <Label>{t('verify.enter_code')}</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  className="tracking-[0.4em] font-mono text-center w-40"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={confirming || code.length < 6}>
                  {confirming ? t('verify.confirming') : t('verify.confirm_code')}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={sendCode} disabled={sending}>
                  {t('verify.resend')}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* ── ID upload ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <IdCard className="w-4 h-4" />{t('verify.id_title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{t('verify.id_help')}</p>
            <IdStatusBadge />
          </div>
          {idStatus === 'rejected' && status?.rejection_reason && (
            <p className="text-sm text-red-600">{status.rejection_reason}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label>{t('verify.id_type')}</Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm h-10"
                value={idType}
                onChange={(e) => setIdType(e.target.value)}
              >
                <option value="dui">DUI</option>
                <option value="passport">{t('verify.passport')}</option>
                <option value="residency">{t('verify.residency')}</option>
              </select>
            </div>
            <input
              ref={idInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={handleIdFile}
            />
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              onClick={() => idInputRef.current?.click()}
              className="h-10"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {uploading ? t('verify.uploading') : hasId ? t('verify.replace_id') : t('verify.upload_id')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">PDF · JPEG · PNG · max 10 MB · {t('verify.private_note')}</p>
        </CardContent>
      </Card>

      <Button variant="ghost" onClick={() => navigate(-1)} className="w-full">
        {t('verify.back')}
      </Button>
    </div>
  );
};

export default ClientVerificationPage;
